import React, {
  ChangeEvent, useEffect, useMemo, useRef, useState,
} from 'react';
import { ConnectedProps, connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { TbFolder, TbFolderOpen, TbFolderPlus } from 'react-icons/tb';
import {
  Drawer,
  Tree,
  Input,
  Heading,
  Select,
  Button,
  Grid,
  Actions,
  Divider,
  Spacer,
  Menu,
  ITreeProps,
  Row,
  Column,
  Badge,
  Text,
} from '@oliasoft-open-source/react-ui-library';
import { AccessControlModal, IAccessControlModalsRef } from '@oliasoft/hierarchy-gui';
import { ActionMap, ability } from '@oliasoft/authorization-ui';
import { getTokenPayload } from '@oliasoft/pkce';
import { FeatureFlags } from '@oliasoft/feature-flags';

import { caslEnums } from 'common/enums/casl-enums';
import { AccessSubjectTypes } from 'common/interfaces/access-rights';
import {
  HierarchyLevelType, IArchive, INode, SecurityClassification,
} from 'common/interfaces/hierarchy-entities';
import translations from 'client/internationalization/translation-map.json';
import {
  addToOpenedItems,
  removeFromOpenedItems,
  resetOpenedItems,
  setSelectedArchiveId,
  setActiveItem,
} from 'client/store/ui/hierarchy-tree/hierarchy-tree';
import { RootState } from 'client/store/configureStore';
import { getUsers } from 'client/store/entities/user/user';
import { getObjectsOfNode } from 'client/store/entities/object/object';
import { getEntityAccessRightsList } from 'client/store/entities/access-rights/access-rights';
import { setAccessRightsBatch } from 'client/components/access-control-modal/access-utils';
import { ClassificationLevelControlModal } from 'client/components/classification-level-control-modal/classification-level-control-modal';
import {
  filterHierarchy, groupHierarchyTreeBy, hierarchyLevelTypeName, hierarchyTypeAddLabel, nodeToTreeItem,
} from 'client/views/explore/hierarchy/hierarchy.utils';
import { GroupCompanyDataBy } from 'client/views/company-data/company-data.interfaces';
import { IHierarchyTreeItem } from 'client/views/explore/hierarchy/hierarchy.interfaces';
import { HierarchyItemStates } from 'client/views/explore/hierarchy/hierarchy-enums';
import { FileUpload } from 'client/components/file-upload/file-upload';
import ClientConfig from 'client/config';
import { WellStatusType } from 'common/enums/entity-enums';
import { COMPANY_DATA_NODE_NAME } from 'common/constants';
import './company-data-drawer.css';
import { getIPMFilesSelector } from 'client/store/entities/ipm-files/selectors';

const getTranslatedGroupByOptions = (t: TFunction) => [
  {
    label: t(translations.companyData_groupByFolder),
    value: GroupCompanyDataBy.Folder,
  },
];

type CompanyDataDrawerProps = PropsFromRedux & {
  hierarchy: { [id: string]: IHierarchyTreeItem };
  groupBy: GroupCompanyDataBy;
  archives: IArchive[];
  setGroupBy: (groupBy: GroupCompanyDataBy) => void;
  createItem: (entityType: HierarchyLevelType, parentItem: IHierarchyTreeItem | null) => void;
  editItem: (item: IHierarchyTreeItem) => void;
  onItemDelete: (item: IHierarchyTreeItem, type: HierarchyItemStates) => void;
  resetOpenedItems: () => void;
  openedItems: { [id: string]: true };
  getNodeObjectsCount: (parentId: string) => number | undefined;
};

const CompanyDataDrawer = ({
  users,
  hierarchy,
  groupBy,
  nodes,
  objects,
  archives,
  selectedArchiveId,
  setSelectedArchiveId,
  setGroupBy,
  createItem,
  editItem,
  onItemDelete,
  activeItem,
  setActiveItem,
  resetOpenedItems,
  openedItems,
  addToOpenedItems,
  removeFromOpenedItems,
  selectedCustomer,
  entityAccessRightsList,
  entityAccessRightsMetadata,
  getEntityAccessRightsList,
  getNodeObjectsCount,
  getObjectsOfNode,
}: CompanyDataDrawerProps) => {
  const { t } = useTranslation();
  const childRef = useRef<IAccessControlModalsRef>(null);

  const tokenPayload = getTokenPayload(
    ClientConfig.config.pkce.provider,
    ClientConfig.config.applicationId,
    ClientConfig.config.idpServerUrl,
  );

  useEffect(() => {
    if (selectedCustomer?.companyName) {
      getUsers();
    }
  }, [selectedCustomer]);

  useEffect(() => {
    childRef.current?.accessRightListUpdated({ accessRightsList: entityAccessRightsList, metadata: entityAccessRightsMetadata });
  }, [entityAccessRightsList]);

  const selectedArchive = selectedArchiveId ? hierarchy[selectedArchiveId] : null;
  const rootNode = nodes.find((node) => node.name === COMPANY_DATA_NODE_NAME && (node as INode & { archiveid?: string }).archiveid === selectedArchiveId) || {} as INode;

  const [isOpen, setOpen] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [nodeId, setNodeId] = useState<string>();
  const [files, setFiles] = useState<File[] | undefined>();
  const [isOpenUploadModal, setOpenUploadModal] = useState<boolean>(false);
  const [isOpenClassificationLevelControlModal, setOpenClassificationLevelControlModal] = useState(false);
  const [classificationLevelHierarchyItem, setClassificationLevelHierarchyItem] = useState<IHierarchyTreeItem | undefined>();
  const ipmFiles = useSelector(getIPMFilesSelector);

  const onFilterInputTextChange = (event: React.ChangeEvent<{ value: string }>) => {
    setFilterText(event.target.value);
  };

  const onGroupByChange = (event: ChangeEvent<{ value: HierarchyLevelType }> | any) => {
    if (groupBy !== event.target.value) {
      resetOpenedItems();
    }
    setGroupBy(event.target.value);
  };

  const collapseTree = () => {
    resetOpenedItems();
  };

  const onChangeArchive = (event: any) => {
    if (selectedArchiveId === event.target.value) return;
    setSelectedArchiveId(event.target.value);
  };

  const archiveItem: IHierarchyTreeItem = {
    ...selectedArchive,
    name: selectedArchive?.name || '',
    id: selectedArchiveId || '',
    key: selectedArchiveId || '',
    entityType: HierarchyLevelType.Archive,
    parent: -1,
  };

  const archiveMenu = archiveItem ? [
    {
      type: 'Option', icon: 'edit', label: t(translations.edit), onClick: () => editItem(archiveItem),
    },
    {
      type: 'Option', icon: 'delete', label: t(translations.delete), onClick: () => onItemDelete(archiveItem, HierarchyItemStates.Delete),
    },
    ...(FeatureFlags.isActive('Multiple Archives') ? [
      { type: 'Divider' },
      {
        type: 'Option',
        icon: 'add',
        label: hierarchyTypeAddLabel[HierarchyLevelType.Archive],
        onClick: () => {
          createItem(HierarchyLevelType.Archive, null);
        },
      },
    ] : []),
  ] : [];

  const actionsCollapse = [{
    icon: 'collapse all',
    onClick: collapseTree,
  }] as any[];

  const actionsAddFolder = [{
    icon: <TbFolderPlus />,
    onClick: () => createItem(HierarchyLevelType.Node, { ...rootNode, id: rootNode?.nodeid, entityType: HierarchyLevelType.Node } as any),
  }] as any[];

  const itemHasChild: ITreeProps['itemHasChild'] = ({ data }) => {
    return data?.entityType === HierarchyLevelType.Node;
  };

  const openUploadModal = (nodeid: string) => {
    setNodeId(nodeid);
    setOpenUploadModal(true);
  };

  const closeUploadModal = () => {
    setOpenUploadModal(false);
    setNodeId(undefined);
    setFiles(undefined);
  };

  const onUploadComplete = (nodeid: string | undefined) => {
    if (nodeid) {
      getObjectsOfNode(nodeid);
    }
    setNodeId(undefined);
    setFiles(undefined);
  };

  const canMangeAccess = ability.can(ActionMap.MANAGE, caslEnums.manageAccessRights);

  const openAccessControlModal = (item: IHierarchyTreeItem, subjectType = AccessSubjectTypes.User) => {
    childRef.current?.accessRightsConfigurationStarted({
      item,
      userList: users,
      currentUserSubjectId: tokenPayload!.subjectId || '',
      subjectType,
    });
    getEntityAccessRightsList(item.entityType, item.id, subjectType);
  };

  const onCloseClassificationLevelControlModal = () => setOpenClassificationLevelControlModal(false);
  const onOpenClassificationLevelControlModal = (item: IHierarchyTreeItem) => {
    setClassificationLevelHierarchyItem(item);
    setOpenClassificationLevelControlModal(true);
  };

  const getAccessManagementAction = (node: INode, disabled = false) => {
    return (canMangeAccess) ? {
      type: 'Option',
      label: t(translations.authorization_accessControl),
      icon: 'key',
      disabled,
      onClick: () => openAccessControlModal(nodeToTreeItem(node)),
    } : null;
  };

  const getClassificationLevelManagementAction = (node: INode, disabled = false) => {
    const canMangeClassificationLevel = ability.can(ActionMap.MANAGE, caslEnums.nodeSecurityClassification);
    return (canMangeClassificationLevel) ? {
      type: 'Option',
      label: t(translations.classificationLevel),
      icon: 'key',
      disabled,
      onClick: () => {
        onOpenClassificationLevelControlModal(nodeToTreeItem(node));
      },
    } : null;
  };

  const ipmFilesNode = {
    archiveid: selectedArchiveId,
    classificationlevel: SecurityClassification.INTERNAL,
    parentid: rootNode?.nodeid,
    nodeid: '-1',
    name: 'IPM Files',
    actions: [],
    role: '',
    created: '',
    updated: '',
    owner: selectedCustomer?.customerLabel,
  } as INode;

  const itemsWithActions = useMemo(() => {
    const dataListItems = [ipmFilesNode, ...nodes].map(node => {
      const isIpmNode = node.nodeid === '-1';
      const accessManagementAction = getAccessManagementAction(node);
      const classificationLevelManagementAction = getClassificationLevelManagementAction(node);
      const count = isIpmNode ? ipmFiles.length : getNodeObjectsCount(node.nodeid) ?? 0;
      const subActions = isIpmNode ? [] : [
        {
          type: 'Option',
          icon: 'add',
          label: `${t(translations.add)} ${t(translations.catalogue)}`,
          onClick: () => createItem(HierarchyLevelType.Node, nodeToTreeItem(node)),
          disabled: node.restricted,
        },
        {
          type: 'Option',
          icon: 'upload',
          label: `${t(translations.importExport_uploadFile)}`,
          onClick: () => openUploadModal(node.nodeid),
          disabled: node.restricted,
        },
        {
          type: 'Option',
          icon: 'edit',
          label: t(translations.edit),
          onClick: () => editItem(nodeToTreeItem(node)),
          disabled: node.restricted,
        },
        {
          type: 'Option',
          icon: 'delete',
          label: t(translations.delete),
          onClick: () => onItemDelete(nodeToTreeItem(node), HierarchyItemStates.Delete),
          disabled: node.restricted,
        },
        ...(accessManagementAction ? [accessManagementAction] : []),
        ...(classificationLevelManagementAction ? [classificationLevelManagementAction] : []),
      ];
      return {
        ...node,
        rawName: node.name,
        id: node.nodeid,
        key: node.nodeid,
        parent: node.parentid === rootNode?.nodeid ? 0 : node.parentid,
        active: node.nodeid === activeItem?.id,
        entityType: HierarchyLevelType.Node,
        onClick: () => setActiveItem(nodeToTreeItem(node)),
        onDrop: undefined,
        actions: [{
          icon: (<Badge color="var(--color-border)" title={count} />),
        },
        {
          subActions,
        }] as any[],
      };
    });

    return dataListItems.sort((a: any, b: any) => {
      return a.rawName.localeCompare(b.rawName);
    });
  }, [nodes, objects, activeItem, rootNode]);

  const renderDataList = () => {
    const onItemToggle = (node: any, isOpen: boolean) => {
      if (isOpen) {
        addToOpenedItems(node.id);
      } else {
        removeFromOpenedItems(node.id);
      }
    };

    const topLevel = HierarchyLevelType.Node;
    const groupedHierarchy = groupHierarchyTreeBy(itemsWithActions, topLevel);
    const treeItems = filterHierarchy(groupedHierarchy, filterText, topLevel);

    const openedTreeItems = [] as string[];
    Object.keys(openedItems)
      .forEach(id => {
        openedTreeItems.push(id);
      });

    const treeItemsWithSubheaders = treeItems.map((item) => {
      const createdBy = item?.createdBy;
      const { applicationId } = ClientConfig.config;

      return {
        ...item,
        name: item.entityType === topLevel ? item.name : (
          <div>
            {item.name}
            {' '}
            <Text faint>{item.entityType === HierarchyLevelType.Design ? <span style={{ color: item.well_status === WellStatusType.Actual ? 'var(--color-info-300)' : '' }}>{item.well_status}</span> : hierarchyLevelTypeName[item.entityType]}</Text>
            {createdBy && createdBy.toLowerCase() !== applicationId.toLowerCase() && (
            <div className="createdByText">
              <Text muted>
                {t(translations.hierarchy_createdBy, { value: createdBy.charAt(0).toUpperCase() + createdBy.slice(1) })}
              </Text>
            </div>
            )}
          </div>
        ),
      };
    });

    return (
      <Tree
        list={{ items: treeItemsWithSubheaders } as any}
        itemHasChild={itemHasChild}
        onItemToggle={onItemToggle}
        isInitialOpen={openedTreeItems}
        autoOpenActiveItems
        icons={{
          expand: <TbFolder />,
          collapse: <TbFolderOpen />,
        }}
      />
    );
  };

  return (
    <>
      <Drawer open={isOpen} setOpen={setOpen} button width={400} closedWidth={50} border background="var(--color-neutral-50)">
        <div className={`drawerContent ${isOpen ? 'visible' : 'hidden'}`}>
          <div style={{ padding: 'var(--padding)' }}>
            <Row>
              <Column>
                <Heading top marginBottom={0}>{t(translations.documents)}</Heading>
              </Column>
              <Column width="var(--padding-l)">
                <Actions actions={actionsAddFolder} />
              </Column>
            </Row>
            <Spacer height="var(--padding-xs)" />
            <Grid columns="90% 0%" gap="var(--padding-sm)">
              <Select
                options={(archives as IArchive[]).map((archiveOption) => ({
                  label: archiveOption.name,
                  value: archiveOption.archiveid,
                }))}
                value={selectedArchiveId}
                onChange={onChangeArchive}
              />
              <Menu menu={{
                sections: archiveMenu,
                trigger: 'Component',
                component: <Button
                  basic
                  colored="muted"
                  round
                  icon="menu"
                  small
                />,
              }}
              />
              <Grid columns="40% 57.5%" gap="var(--padding-xs)">
                <Select
                  small
                  value={groupBy}
                  onChange={onGroupByChange}
                  options={getTranslatedGroupByOptions(t)}
                />
                <Input
                  small
                  type="search"
                  placeholder={t(translations.companyData_search)}
                  onChange={onFilterInputTextChange}
                  value={filterText}
                />
              </Grid>
              <Actions actions={actionsCollapse} />
            </Grid>
          </div>
          <Divider margin={0} />
          <div>
            {renderDataList()}
          </div>
        </div>
      </Drawer>

      {nodeId && isOpenUploadModal
        && (
        <FileUpload
          isModal
          visible={isOpenUploadModal}
          onClose={closeUploadModal}
          initialNodeId={nodeId || activeItem?.id}
          onUploadComplete={() => onUploadComplete(nodeId)}
          archiveid={selectedArchiveId!}
          initialFiles={files}
        />
        )}

      <AccessControlModal
        ref={childRef}
        handleEntityAccessRightsUpdated={setAccessRightsBatch}
      />
      {
        isOpenClassificationLevelControlModal ? (
          <ClassificationLevelControlModal
            onCloseClassificationLevelControlModal={onCloseClassificationLevelControlModal}
            showModal={isOpenClassificationLevelControlModal}
            selectedItem={classificationLevelHierarchyItem}
          />
        ) : null
      }
    </>
  );
};

const mapStateToProps = ({ ui, entities }: RootState) => ({
  openedItems: ui.hierarchyTree.openedItems,
  selectedArchiveId: ui.hierarchyTree.selectedArchiveId,
  activeItem: ui.hierarchyTree.activeItem,
  users: entities?.user.users,
  nodes: entities?.nodeTree.items,
  objects: entities?.object.objects,
  entityAccessRightsList: entities?.entityAccessRights.accessRights,
  entityAccessRightsMetadata: entities?.entityAccessRights.metadata,
  selectedCustomer: entities?.customer.selectedCustomer,
});

const mapDispatchToProps = {
  addToOpenedItems,
  removeFromOpenedItems,
  resetOpenedItems,
  setSelectedArchiveId,
  setActiveItem,
  getEntityAccessRightsList,
  getObjectsOfNode,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(CompanyDataDrawer);

export { Container as CompanyDataDrawer };
